import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, LogIn, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SearchOverlay from './SearchOverlay';
import ExhibitionHeaderBadge from './ExhibitionHeaderBadge';
import { isExhibitionActive, openExhibitionModal } from './ExhibitionModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'ru', label: 'RU' },
    { code: 'kz', label: 'KZ' },
    { code: 'en', label: 'EN' },
    { code: 'ar', label: 'AR' }
  ];

  const currentLang = languages.find(lang => lang.code === language);

  const navItems = [
    { path: '/', label: { ru: 'Главная', kz: 'Басты бет', en: 'Home', ar: 'الرئيسية' } },
    { path: '/about', label: { ru: 'О нас', kz: 'Біз туралы', en: 'About Us', ar: 'من نحن' } },
    { path: '/products', label: { ru: 'Продукция', kz: 'Өнімдер', en: 'Products', ar: 'المنتجات' } },
    { path: '/catalog', label: { ru: 'Каталог', kz: 'Каталог', en: 'Catalog', ar: 'الكتالوج' } },
    { path: '/installation', label: { ru: 'Монтаж', kz: 'Монтаж', en: 'Installation', ar: 'التركيب' } },
    { path: '/video', label: { ru: 'Видео', kz: 'Бейне', en: 'Video', ar: 'فيديو' } },
    { path: '/certificates', label: { ru: 'Сертификаты', kz: 'Сертификаттар', en: 'Certificates', ar: 'الشهادات' } },
    { path: '/agsk3', label: { ru: 'АГСК-3', kz: 'АГСК-3', en: 'AGSK-3', ar: 'AGSK-3' } },
    { path: '/contact', label: { ru: 'Контакты', kz: 'Байланыс', en: 'Contact', ar: 'تواصل معنا' } }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#061d36] ${
        isScrolled 
          ? 'shadow-lg py-2' 
          : 'py-3'
      }`}
    >
      <div className="max-w-[1920px] mx-auto px-2 lg:px-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-1.5 group flex-shrink-0">
            <img 
              src="/logo-icon-hq.png" 
              alt="GIPSOVINIL.KZ" 
              className="h-9 w-auto"
            />
            <img 
              src="/logo-text-hq.png" 
              alt="GIPSOVINIL.KZ" 
              className="h-4 sm:h-5 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-0.5 xl:space-x-1.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2 xl:px-3 py-1.5 rounded-lg font-semibold transition-all text-xs xl:text-sm whitespace-nowrap ${
                  location.pathname === item.path
                    ? 'bg-white/15 text-white font-bold border-b-2 border-blue-400'
                    : 'text-white font-semibold hover:bg-white/10'
                }`}
              >
                {item.label[language]}
              </Link>
            ))}
          </nav>

          {/* Cabinet, Search, Language & Mobile Menu */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
            {/* Exhibition Badge - KazBuild 2026 */}
            <ExhibitionHeaderBadge />
            {/* Dealer Cabinet Button - desktop */}
            <Link to="/dealer/login" className="hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-lg bg-[#8b4444] hover:bg-[#7a3838] transition-all text-white text-xs font-medium" data-testid="dealer-cabinet-btn" title={language === 'ru' ? 'Кабинет Дилера' : language === 'ar' ? 'حساب الموزع' : 'Dealer Cabinet'}>
              <LogIn className="w-3.5 h-3.5" />
              <span className="hidden xl:inline">{language === 'ru' ? 'Кабинет' : language === 'kz' ? 'Кабинет' : language === 'ar' ? 'الحساب' : 'Cabinet'}</span>
            </Link>
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-lg border border-white/20 hover:border-white/40 hover:bg-white/10 transition-all text-white"
              data-testid="header-search-btn"
              title={language === 'ru' ? 'Поиск по сайту' : language === 'ar' ? 'البحث في الموقع' : 'Search'}
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center space-x-1 px-2 py-2 rounded-lg hover:bg-white/10 transition-all text-white text-sm font-medium"
              >
                <Globe className="w-4 h-4 text-blue-300" />
                <span>{currentLang?.label}</span>
              </button>

              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-28 bg-white rounded-lg shadow-xl py-1 z-50 border border-gray-100">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-[#061d36]/10 transition-colors text-sm ${
                        language === lang.code ? 'bg-[#061d36]/10 text-[#061d36] font-bold' : 'text-gray-700'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg border border-white/20 hover:border-white/40 hover:bg-white/10 transition-all text-white"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-left px-4 py-3 rounded-lg font-semibold transition-all ${
                    location.pathname === item.path
                      ? 'bg-white/15 text-white font-bold border-l-4 border-blue-400'
                      : 'text-white font-semibold hover:bg-white/10'
                  }`}
                >
                  {item.label[language]}
                </Link>
              ))}
              <Link
                to="/dealer/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left px-4 py-3 rounded-lg font-semibold transition-all bg-[#8b4444] text-white hover:bg-[#7a3838] flex items-center gap-2 mt-2"
              >
                <LogIn className="w-4 h-4" />
                {language === 'ru' ? 'Кабинет Дилера' : language === 'kz' ? 'Дилер Кабинеті' : language === 'ar' ? 'حساب الموزع' : 'Dealer Cabinet'}
              </Link>
              {isExhibitionActive() && (
                <button
                  onClick={() => { setIsMobileMenuOpen(false); openExhibitionModal(); }}
                  className="text-left px-4 py-3 rounded-lg font-bold transition-all bg-gradient-to-r from-[#8b4444] to-[#a94f4f] text-white flex items-center gap-2 mt-2"
                  data-testid="exhibition-mobile-link"
                >
                  🎟️ {language === 'ru' ? 'KazBuild 2–4 сен · Приглашение' : language === 'kz' ? 'KazBuild 2–4 қыр · Шақыру' : language === 'ar' ? 'KazBuild 2–4 سبتمبر · دعوة' : 'KazBuild Sep 2–4 · Invite'}
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
};

export default Header;
